import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  CustomInput,
  Col,
  Form,
  FormGroup,
  ListGroup,
  ListGroupItem,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
} from "reactstrap";
import { usersApi } from "../../../services/userServices";

const WAITING_FOR_ASSIGNMENT_STATUS = 1;
const WAITING_FOR_PROGRAMMING_STATUS = 2;
const PROGRAMMING_STATUS = 3;
const WAITING_FOR_CHECK_STATUS = 4;
const CHECKING_STATUS = 5;
const PROGRAMMING_COMPLETE_STATUS = 6;

const PROGRAMMING_STATUSES = [
  { status: "Waiting for Assignment", value: WAITING_FOR_ASSIGNMENT_STATUS },
  { status: "Waiting for Programming", value: WAITING_FOR_PROGRAMMING_STATUS },
  { status: "Programming", value: PROGRAMMING_STATUS },
  { status: "Waiting for Check", value: WAITING_FOR_CHECK_STATUS },
  { status: "Checking", value: CHECKING_STATUS },
  { status: "Programming Complete", value: PROGRAMMING_COMPLETE_STATUS },
];
const DEFAULT_PROGRAMMING_STATUSES = [
  { status: "Waiting for Assignment", value: WAITING_FOR_ASSIGNMENT_STATUS },
];

const ProgrammingDetailsModal = ({
  programming,
  detailModal,
  onSubmit,
  onClear,
  readOnly,
  setInformationModal = () => {},
  checkerId,
  setCheckerId = () => {},
  programmerId,
  setProgrammerId = () => {},
  enableUpdate,
  statusId,
  setStatusId = () => {},
}) => {
  const [users, setUsers] = useState();
  const [checkers, setCheckers] = useState([]);
  const [programmers, setProgrammers] = useState([]);

  const programmer = readOnly
    ? programming?.programmer
      ? `${programming?.programmer?.firstName} ${programming?.programmer?.lastName}`
      : "-"
    : programmerId;
  const checker = readOnly
    ? programming?.checker
      ? `${programming?.checker?.firstName} ${programming?.checker?.lastName}`
      : "-"
    : checkerId;

  const optionChange = useCallback(
    (userId, isProgrammer, usersList = users) => {
      if (!usersList) return;

      const usersFiltered = usersList.filter((user) => user?.id !== +userId);
      if (isProgrammer) {
        setCheckers(usersFiltered);
        setProgrammerId(userId || null);
      } else {
        setProgrammers(usersFiltered);
        setCheckerId(userId || null);
      }
    },
    [users, setCheckerId, setProgrammerId]
  );

  const assignmentValidation = readOnly
    ? programming?.programmer && programming?.checker
    : checkerId && checkerId !== "-" && programmerId && programmerId !== "-";

  // Load users with Programmer role (ID: 6) only once
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (detailModal && !readOnly && !users) {
          const data = await usersApi.getUsers({ pageSize: 10000, roleId: 6 });
          setUsers(data.data);
        }
      } catch (err) {
        if (typeof setInformationModal === "function") {
          setInformationModal({
            isOpen: true,
            title: "Error",
            body: "There was an error loading users.",
          });
        }
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailModal, readOnly, users]);

  // Initialize user options when users are loaded or props change
  useEffect(() => {
    if (users && users.length > 0) {
      optionChange(programmerId, true, users);
      optionChange(checkerId, false, users);
    }
  }, [users, programmerId, checkerId, optionChange]);

  return (
    <Modal isOpen={detailModal} className="modal-sm">
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <Row className="mt-2 mx-2">
            <ListGroup className="w-100">
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Customer</span>
                <span>{programming?.job?.customer?.name || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Job#</span>
                <span>{programming?.job?.jobNumber || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Project Manager</span>
                <span>
                  {programming?.job?.manager
                    ? `${programming?.job?.manager?.firstName} ${programming?.job?.manager?.lastName}`
                    : "-"}
                </span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Due Date</span>
                <span>{programming?.revisionDate || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Job Desc.</span>
                <span>{programming?.description || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">PM Notes</span>
                <span>{programming?.notes || "-"}</span>
              </ListGroupItem>
              <ListGroupItem className="d-flex justify-content-between">
                <span className="font-weight-bold">Review Process</span>
                <span>{programming?.reviewProcess?.description || "-"}</span>
              </ListGroupItem>
              <ListGroupItem>
                <FormGroup className="w-100 d-flex justify-content-between mb-0">
                  <Label className="mb-0 font-weight-bold" for="programmer">
                    Programmer
                  </Label>
                  {readOnly ? (
                    <span>{programmer || "-"}</span>
                  ) : (
                    <CustomInput
                      className="d-inline-block w-auto"
                      type="select"
                      id="programmer"
                      value={programmerId || ""}
                      onChange={(e) => optionChange(e.target.value, true)}
                    >
                      <option value="">Select Programmer</option>
                      {programmers
                        .filter((programmer) => programmer?.id !== +checkerId)
                        .map((programmer) => (
                          <option
                            value={programmer.id}
                            key={`programmer-${programmer.id}`}
                          >{`${programmer?.firstName} ${programmer?.lastName}`}</option>
                        ))}
                    </CustomInput>
                  )}
                </FormGroup>
              </ListGroupItem>
              <ListGroupItem>
                <FormGroup className="w-100 d-flex justify-content-between mb-0">
                  <Label className="mb-0 font-weight-bold" for="checker">
                    Checker
                  </Label>
                  {readOnly ? (
                    <span>{checker || "-"}</span>
                  ) : (
                    <CustomInput
                      className="d-inline-block w-auto"
                      type="select"
                      id="checker"
                      value={checkerId || ""}
                      onChange={(e) => optionChange(e.target.value)}
                    >
                      <option value="">Select Checker</option>
                      {checkers
                        .filter((checker) => checker?.id !== +programmerId)
                        .map((checker) => (
                          <option
                            value={checker.id}
                            key={`checker-${checker.id}`}
                          >{`${checker?.firstName} ${checker?.lastName}`}</option>
                        ))}
                    </CustomInput>
                  )}
                </FormGroup>
              </ListGroupItem>
              {enableUpdate ? (
                <ListGroupItem>
                  <FormGroup className="w-100 d-flex justify-content-between mb-0">
                    <Label className="mb-0 font-weight-bold" for="status">
                      Status
                    </Label>
                    <CustomInput
                      className="d-inline-block w-auto"
                      type="select"
                      id="status"
                      value={statusId || ""}
                      onChange={(e) => setStatusId(+e.target.value)}
                    >
                      <option value={""} disabled>
                        Select A Status
                      </option>
                      {(assignmentValidation
                        ? PROGRAMMING_STATUSES
                        : DEFAULT_PROGRAMMING_STATUSES
                      ).map((item) => (
                        <option value={item.value} key={`item-${item.value}`}>
                          {item.status}
                        </option>
                      ))}
                    </CustomInput>
                  </FormGroup>
                </ListGroupItem>
              ) : null}
            </ListGroup>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Col>
            <Row>
              {readOnly && !enableUpdate ? (
                <Button
                  color={"secondary"}
                  onClick={() => {
                    onClear();
                  }}
                >
                  Close
                </Button>
              ) : (
                <div className="d-flex justify-content-between w-100">
                  <Button
                    color={"secondary"}
                    onClick={() => {
                      onClear();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button color={"primary"} type="submit">
                    Save
                  </Button>
                </div>
              )}
            </Row>
          </Col>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ProgrammingDetailsModal;
